import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { AvatarGroup, type AvatarItem } from "@tellescope/ui";
import { formatInitials, formatName, formatNames } from "@tellescope/utils";
import { toast } from "sonner";
import { useOpenChatThread } from "../hooks";
import { MessageCircle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export interface ChatNotificationProps {
  message: string;
  chatroom: TellescopeModelFromName<"chat_rooms">;
  endusers?: TellescopeModelFromName<"endusers">[];
  toastId?: string | number;
  teamChat?: boolean;
}

export const ChatNotification = ({ message, chatroom, endusers = [], toastId, teamChat }: ChatNotificationProps) => {
  const { t } = useTranslation("notifications");
  const openChatThread = useOpenChatThread();

  const handleClick = async () => {
    await openChatThread(chatroom.id);
    if (toastId !== undefined) {
      toast.dismiss(toastId);
    }
  };

  const displayName = useMemo(() => {
    if (chatroom.title) return chatroom.title;
    return formatNames(endusers) || t("Unknown Contact");
  }, [chatroom.title, endusers, t]);

  const avatarItems: AvatarItem[] = useMemo(
    () =>
      endusers.map((enduser) => ({
        id: enduser.id,
        name: formatName(enduser),
        avatar: enduser.avatar,
        initials: formatInitials(enduser),
      })),
    [endusers]
  );

  if (teamChat) {
    return (
      <Link
        to={`/messaging/team-chat/${chatroom.id}`}
        className="flex gap-3 items-center cursor-pointer hover:bg-accent p-4 rounded-lg bg-background border border-border shadow-lg transition-colors min-w-[320px]"
        tabIndex={0}
      >
        <AvatarGroup items={avatarItems} maxVisible={3} size="md" />

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <p className="font-semibold text-sm truncate">{displayName}</p>
          </div>

          <p className="text-sm text-muted-foreground line-clamp-2">{message}</p>
        </div>
      </Link>
    );
  }

  return (
    <div
      onClick={handleClick}
      className="flex gap-3 items-center cursor-pointer hover:bg-accent p-4 rounded-lg bg-background border border-border shadow-lg transition-colors min-w-[320px]"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <AvatarGroup items={avatarItems} maxVisible={3} size="md" />

      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <p className="font-semibold text-sm truncate">{displayName}</p>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2">{message}</p>
      </div>
    </div>
  );
};
